import type { PrintTemplateSection } from "@repo/types";
import type {
  PrintFieldDef,
  PrintSectionDef,
} from "@repo/print-pdf/arbre/print/template";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@repo/ui/components/ui/button";
import { Input } from "@repo/ui/components/ui/input";
import { Switch } from "@repo/ui/components/ui/switch";
import { ChevronDown, ChevronRight, GripVertical } from "lucide-react";
import { useState } from "react";
import { ColorField } from "./color-field";
import { ColumnsEditor } from "./columns-editor";
import type { DraftDispatch } from "./use-template-draft";

/** Nom affiché dans l'éditeur pour les sections sans barre de titre sur la fiche. */
export const UNTITLED_SECTION_NAMES: Record<string, string> = {
  identification: "Identification",
  commentaires: "Commentaires",
  adresse: "Adresse",
  risque_age: "Risque global et âge",
  photos: "Photos",
  commentaire: "Commentaire général",
  resume: "Résumé",
};

/** `{n}` (numéro de l'essence) s'affiche « N » dans l'éditeur. */
export const sectionName = (def: PrintSectionDef) =>
  (def.title || UNTITLED_SECTION_NAMES[def.id] || def.id).replace("{n}", "N");

interface Props {
  section: PrintTemplateSection;
  def: PrintSectionDef;
  fieldByKey: Map<string, PrintFieldDef<any>>;
  dispatch: DraftDispatch;
}

export function SectionEditor({ section, def, fieldByKey, dispatch }: Props) {
  const [open, setOpen] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? "opacity-40" : undefined}
    >
      <div className="rounded-md border border-border bg-card">
        <div className="flex items-center gap-2 p-2">
          <button
            type="button"
            aria-label={`Déplacer la section ${sectionName(def)}`}
            className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Replier" : "Déplier"}
          >
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
          <Input
            value={section.title ?? ""}
            placeholder={sectionName(def)}
            onChange={(e) =>
              dispatch({
                type: "setSectionTitle",
                id: section.id,
                title: e.target.value,
              })
            }
            className="h-8 flex-1"
            aria-label="Titre de la section"
          />
          <select
            value={section.columns}
            onChange={(e) =>
              dispatch({
                type: "setSectionColumns",
                id: section.id,
                columns: Number(e.target.value),
              })
            }
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            aria-label="Nombre de colonnes"
          >
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n} col.
              </option>
            ))}
          </select>
          <Switch
            checked={section.visible}
            onCheckedChange={() =>
              dispatch({ type: "toggleSection", id: section.id })
            }
            aria-label="Afficher la section"
          />
        </div>

        {open && (
          <div className="space-y-2 border-t border-border p-2">
            <div className="space-y-1 rounded-md bg-muted/40 p-2">
              <ColorField
                label="Fond du titre"
                value={section.style?.titleBg}
                fallback="#808080"
                onChange={(value) =>
                  dispatch({
                    type: "setSectionStyle",
                    id: section.id,
                    key: "titleBg",
                    value,
                  })
                }
              />
              <ColorField
                label="Texte du titre"
                value={section.style?.titleColor}
                fallback="#FFFFFF"
                onChange={(value) =>
                  dispatch({
                    type: "setSectionStyle",
                    id: section.id,
                    key: "titleColor",
                    value,
                  })
                }
              />
              <ColorField
                label="Fond du contenu"
                value={section.style?.background}
                fallback="#FFFFFF"
                onChange={(value) =>
                  dispatch({
                    type: "setSectionStyle",
                    id: section.id,
                    key: "background",
                    value,
                  })
                }
              />
            </div>
            <ColumnsEditor
              section={section}
              fieldByKey={fieldByKey}
              dispatch={dispatch}
            />
          </div>
        )}
      </div>
    </div>
  );
}
